import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, Settings, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle,
  Server, Shield, Link2, Play, Eye, Download, History, Plug,
  Building2, UserCheck, UserX, UserPlus, ArrowRight, Zap, Database
} from 'lucide-react';

const SYNC_MODES = [
  { value: 'disabled', label: 'Disabled', color: 'bg-slate-800 text-slate-400', description: 'LDAP sync is turned off' },
  { value: 'simulation', label: 'Simulation', color: 'bg-amber-900/30 text-amber-400', description: 'Test with mock AD data' },
  { value: 'live', label: 'Live', color: 'bg-emerald-900/30 text-emerald-400', description: 'Connected to real AD server' },
];

const SYNC_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-slate-800 text-slate-400', icon: Clock },
  running: { label: 'Running', color: 'bg-sky-900/30 text-sky-400', icon: RefreshCw },
  completed: { label: 'Completed', color: 'bg-emerald-900/30 text-emerald-400', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-900/30 text-red-400', icon: XCircle },
  partial: { label: 'Partial', color: 'bg-amber-900/30 text-amber-400', icon: AlertTriangle },
};

export default function LDAPSyncPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // State
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [adUsers, setAdUsers] = useState([]);
  const [adGroups, setAdGroups] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncPreview, setSyncPreview] = useState(null);
  const [securityGroups, setSecurityGroups] = useState([]);
  
  // Dialogs
  const [showSettings, setShowSettings] = useState(false);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Auto-sync status
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);
  
  // Form state for settings
  const [editConfig, setEditConfig] = useState(null);
  const [newMapping, setNewMapping] = useState({
    ad_group_dn: '',
    ad_group_name: '',
    security_group_id: '',
    security_group_name: '',
    auto_assign_roles: []
  });

  const fetchData = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const [configRes, statsRes, historyRes, groupsRes, autoSyncRes] = await Promise.all([
        api.get('/ldap/config'),
        api.get('/ldap/stats'),
        api.get('/ldap/sync/history'),
        api.get('/user-management/security-groups'),
        api.get('/ldap/auto-sync/status'),
      ]);
      setConfig(configRes.data);
      setStats(statsRes.data);
      setSyncHistory(historyRes.data || []);
      setSecurityGroups(groupsRes.data || []);
      setAutoSyncStatus(autoSyncRes.data);
    } catch (error) {
      console.error('Error fetching LDAP data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchAdData = useCallback(async () => {
    if (!api) return;
    try {
      const [usersRes, groupsRes] = await Promise.all([
        api.get('/ldap/ad-users'),
        api.get('/ldap/ad-groups'),
      ]);
      setAdUsers(usersRes.data?.users || []);
      setAdGroups(groupsRes.data?.groups || []);
    } catch (error) {
      console.error('Error fetching AD data:', error);
    }
  }, [api]);

  const fetchPreview = useCallback(async () => {
    if (!api) return;
    try {
      const res = await api.get('/ldap/sync/preview');
      setSyncPreview(res.data);
    } catch (error) {
      console.error('Error fetching sync preview:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'ad-users') {
      fetchAdData();
    } else if (activeTab === 'preview') {
      fetchPreview();
    }
  }, [activeTab, fetchAdData, fetchPreview]);

  const handleInitialize = async () => {
    try {
      await api.post('/ldap/initialize');
      toast.success('LDAP configuration initialized with simulation mode');
      fetchData();
    } catch (error) {
      toast.error('Failed to initialize LDAP');
    }
  };

  const handleTestConnection = async () => {
    try {
      const res = await api.post('/ldap/config/test');
      if (res.data.success) {
        toast.success(`Connection successful! Mode: ${res.data.mode}`);
      } else {
        toast.error(res.data.error || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    }
  };

  const handleRunSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/ldap/sync/run');
      toast.success(`Sync completed! Created: ${res.data.users_created}, Updated: ${res.data.users_updated}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleAutoSync = async (enabled) => {
    try {
      const interval = config?.sync_interval_hours || 24;
      await api.post(`/ldap/auto-sync/toggle?enabled=${enabled}&interval_hours=${interval}`);
      toast.success(enabled ? 'Auto-sync enabled' : 'Auto-sync disabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle auto-sync');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await api.put('/ldap/config', editConfig);
      toast.success('Configuration saved');
      setShowSettings(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const handleAddGroupMapping = async () => {
    try {
      const selectedGroup = securityGroups.find(g => g.id === newMapping.security_group_id);
      const payload = {
        ...newMapping,
        security_group_name: selectedGroup?.name || ''
      };
      await api.post('/ldap/group-mappings', payload);
      toast.success('Group mapping added');
      setShowAddMapping(false);
      setNewMapping({ ad_group_dn: '', ad_group_name: '', security_group_id: '', security_group_name: '', auto_assign_roles: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to add mapping');
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!confirm('Delete this group mapping?')) return;
    try {
      await api.delete(`/ldap/group-mappings/${mappingId}`);
      toast.success('Mapping deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete mapping');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  const modeConfig = SYNC_MODES.find(m => m.value === config?.sync_mode) || SYNC_MODES[0];

  return (
    <div className="space-y-6" data-testid="ldap-sync-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-7 h-7 text-violet-400" />
            Active Directory Sync
          </h1>
          <p className="text-slate-400 mt-1">Sync users and groups from Active Directory / LDAP</p>
        </div>
        <div className="flex gap-2">
          {!stats?.config_exists && (
            <Button onClick={handleInitialize} className="bg-violet-600 hover:bg-violet-700">
              <Zap className="w-4 h-4 mr-2" />
              Initialize
            </Button>
          )}
          {stats?.config_exists && (
            <>
              <Button variant="outline" onClick={handleTestConnection}>
                <Plug className="w-4 h-4 mr-2" />
                Test Connection
              </Button>
              <Button variant="outline" onClick={() => { setEditConfig(config); setShowSettings(true); }}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button 
                onClick={handleRunSync} 
                disabled={syncing || config?.sync_mode === 'disabled'}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {syncing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Run Sync
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Sync Mode</p>
                  <Badge className={`${modeConfig.color} border border-current mt-1`}>
                    {modeConfig.label}
                  </Badge>
                </div>
                <Server className="w-8 h-8 text-violet-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">LDAP Users</p>
                  <p className="text-2xl font-bold text-white">{stats.ldap_users_total}</p>
                </div>
                <Users className="w-8 h-8 text-sky-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active</p>
                  <p className="text-2xl font-bold text-emerald-400">{stats.ldap_users_active}</p>
                </div>
                <UserCheck className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Group Mappings</p>
                  <p className="text-2xl font-bold text-amber-400">{stats.group_mappings_count}</p>
                </div>
                <Link2 className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Last Sync</p>
                  <p className="text-sm text-white">
                    {stats.last_sync_at 
                      ? new Date(stats.last_sync_at).toLocaleDateString() 
                      : 'Never'}
                  </p>
                </div>
                <History className="w-8 h-8 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Auto-Sync Status Banner */}
      {autoSyncStatus?.configured && (
        <Card className={`border ${autoSyncStatus.auto_sync_enabled ? 'bg-emerald-950/30 border-emerald-800' : 'bg-slate-900/60 border-slate-800'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-full ${autoSyncStatus.auto_sync_enabled ? 'bg-emerald-900/50' : 'bg-slate-800'}`}>
                  <Clock className={`w-5 h-5 ${autoSyncStatus.auto_sync_enabled ? 'text-emerald-400' : 'text-slate-500'}`} />
                </div>
                <div>
                  <p className="font-medium text-white">
                    Scheduled Auto-Sync: {autoSyncStatus.auto_sync_enabled ? 'Enabled' : 'Disabled'}
                  </p>
                  {autoSyncStatus.auto_sync_enabled && autoSyncStatus.next_sync_at && (
                    <p className="text-sm text-slate-400">
                      Next sync: {new Date(autoSyncStatus.next_sync_at).toLocaleString()} 
                      (every {autoSyncStatus.sync_interval_hours}h)
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-400">
                  {autoSyncStatus.auto_sync_enabled ? 'Disable' : 'Enable'} auto-sync
                </span>
                <Switch 
                  checked={autoSyncStatus.auto_sync_enabled}
                  onCheckedChange={handleToggleAutoSync}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-slate-800">
            <Server className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="ad-users" className="data-[state=active]:bg-slate-800">
            <Users className="w-4 h-4 mr-2" />
            AD Users
          </TabsTrigger>
          <TabsTrigger value="mappings" className="data-[state=active]:bg-slate-800">
            <Link2 className="w-4 h-4 mr-2" />
            Group Mappings
          </TabsTrigger>
          <TabsTrigger value="preview" className="data-[state=active]:bg-slate-800">
            <Eye className="w-4 h-4 mr-2" />
            Sync Preview
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-slate-800">
            <History className="w-4 h-4 mr-2" />
            Sync History
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Connection Status */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plug className="w-5 h-5 text-violet-400" />
                  Connection Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <span className="text-slate-400">Server</span>
                  <span className="text-white font-mono text-sm">{config?.server_url || 'Not configured'}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <span className="text-slate-400">Base DN</span>
                  <span className="text-white font-mono text-sm">{config?.base_dn || 'Not configured'}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <span className="text-slate-400">Mode</span>
                  <Badge className={`${modeConfig.color} border border-current`}>{modeConfig.label}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <span className="text-slate-400">Auto-Sync</span>
                  <Badge className={config?.auto_sync_enabled ? 'bg-emerald-900/30 text-emerald-400' : 'bg-slate-800 text-slate-400'}>
                    {config?.auto_sync_enabled ? `Every ${config?.sync_interval_hours}h` : 'Disabled'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Sync Settings - Editable */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-cyan-400" />
                  Sync Settings
                </CardTitle>
                <CardDescription>Configure how users are synced from AD</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div>
                    <p className="text-white font-medium">Auto-Create Users</p>
                    <p className="text-sm text-slate-500">Automatically create users found in AD</p>
                  </div>
                  <Switch 
                    checked={config?.auto_create_users || false}
                    onCheckedChange={async (checked) => {
                      try {
                        await api.put('/ldap/config', { auto_create_users: checked });
                        toast.success(checked ? 'Auto-create enabled' : 'Auto-create disabled');
                        fetchData();
                      } catch (e) {
                        toast.error('Failed to update setting');
                      }
                    }}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div>
                    <p className="text-white font-medium">Auto-Deactivate Users</p>
                    <p className="text-sm text-slate-500">Deactivate users removed from AD</p>
                  </div>
                  <Switch 
                    checked={config?.auto_deactivate_users || false}
                    onCheckedChange={async (checked) => {
                      try {
                        await api.put('/ldap/config', { auto_deactivate_users: checked });
                        toast.success(checked ? 'Auto-deactivate enabled' : 'Auto-deactivate disabled');
                        fetchData();
                      } catch (e) {
                        toast.error('Failed to update setting');
                      }
                    }}
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div>
                    <p className="text-white font-medium">Sync Interval</p>
                    <p className="text-sm text-slate-500">How often to run auto-sync</p>
                  </div>
                  <Select 
                    value={String(config?.sync_interval_hours || 24)}
                    onValueChange={async (value) => {
                      try {
                        await api.put('/ldap/config', { sync_interval_hours: parseInt(value) });
                        toast.success(`Sync interval set to ${value} hours`);
                        fetchData();
                      } catch (e) {
                        toast.error('Failed to update interval');
                      }
                    }}
                  >
                    <SelectTrigger className="w-[140px] bg-slate-950 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="1">Every 1 hour</SelectItem>
                      <SelectItem value="6">Every 6 hours</SelectItem>
                      <SelectItem value="12">Every 12 hours</SelectItem>
                      <SelectItem value="24">Every 24 hours</SelectItem>
                      <SelectItem value="48">Every 48 hours</SelectItem>
                      <SelectItem value="168">Every week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div>
                    <p className="text-white font-medium">Default Roles</p>
                    <p className="text-sm text-slate-500">Roles assigned to new users</p>
                  </div>
                  <div className="flex gap-1">
                    {(config?.default_roles || ['employee']).map(role => (
                      <Badge key={role} className="bg-sky-900/30 text-sky-400 border border-sky-800 capitalize">{role}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div>
                    <p className="text-white font-medium">Attribute Mappings</p>
                    <p className="text-sm text-slate-500">LDAP fields mapped to user fields</p>
                  </div>
                  <Badge className="bg-slate-800 text-slate-300">{config?.attribute_mappings?.length || 0} fields</Badge>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => { setEditConfig(config); setShowSettings(true); }}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Advanced Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AD Users Tab */}
        <TabsContent value="ad-users" className="mt-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-sky-400" />
                Active Directory Users
                {config?.sync_mode === 'simulation' && (
                  <Badge className="bg-amber-900/30 text-amber-400 ml-2">Simulated Data</Badge>
                )}
              </CardTitle>
              <CardDescription>Users available for sync from Active Directory</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border border-slate-800 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400">Department</TableHead>
                      <TableHead className="text-slate-400">Title</TableHead>
                      <TableHead className="text-slate-400">Groups</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No AD users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      adUsers.map((user, idx) => (
                        <TableRow key={idx} className="border-slate-800 hover:bg-slate-800/50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-white">{user.displayName}</p>
                              {user.displayNameAr && <p className="text-sm text-slate-400">{user.displayNameAr}</p>}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-300 font-mono text-sm">{user.mail}</TableCell>
                          <TableCell className="text-slate-300">{user.department}</TableCell>
                          <TableCell className="text-slate-300">{user.title}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(user.memberOf || []).slice(0, 2).map((g, i) => (
                                <Badge key={i} className="bg-slate-800 text-slate-400 text-xs">
                                  {g.split(',')[0].replace('CN=', '')}
                                </Badge>
                              ))}
                              {(user.memberOf || []).length > 2 && (
                                <Badge className="bg-slate-800 text-slate-400 text-xs">
                                  +{user.memberOf.length - 2}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Group Mappings Tab */}
        <TabsContent value="mappings" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowAddMapping(true)} className="bg-violet-600 hover:bg-violet-700">
              <Link2 className="w-4 h-4 mr-2" />
              Add Mapping
            </Button>
          </div>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-amber-400" />
                AD Group → Security Group Mappings
              </CardTitle>
              <CardDescription>Map Active Directory groups to AeroGuard security groups</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border border-slate-800 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-slate-400">AD Group</TableHead>
                      <TableHead className="text-slate-400 w-[40px]"></TableHead>
                      <TableHead className="text-slate-400">Security Group</TableHead>
                      <TableHead className="text-slate-400">Auto-Assign Roles</TableHead>
                      <TableHead className="text-slate-400 w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(config?.group_mappings || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No group mappings configured
                        </TableCell>
                      </TableRow>
                    ) : (
                      (config?.group_mappings || []).map((mapping) => (
                        <TableRow key={mapping.id} className="border-slate-800 hover:bg-slate-800/50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-white">{mapping.ad_group_name}</p>
                              <p className="text-xs text-slate-500 font-mono">{mapping.ad_group_dn}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <ArrowRight className="w-4 h-4 text-slate-500" />
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-violet-900/30 text-violet-400 border border-violet-800">
                              {mapping.security_group_name}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(mapping.auto_assign_roles || []).map(role => (
                                <Badge key={role} className="bg-sky-900/30 text-sky-400 text-xs capitalize">
                                  {role}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteMapping(mapping.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Preview Tab */}
        <TabsContent value="preview" className="mt-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-cyan-400" />
                    Sync Preview
                  </CardTitle>
                  <CardDescription>Preview what will happen on the next sync</CardDescription>
                </div>
                <Button variant="outline" onClick={fetchPreview}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {syncPreview ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-emerald-900/20 border border-emerald-800">
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-emerald-400" />
                        <span className="text-emerald-400">To Create</span>
                      </div>
                      <p className="text-3xl font-bold text-white mt-2">{syncPreview.users_to_create}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-sky-900/20 border border-sky-800">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-sky-400" />
                        <span className="text-sky-400">To Update</span>
                      </div>
                      <p className="text-3xl font-bold text-white mt-2">{syncPreview.users_to_update}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-red-900/20 border border-red-800">
                      <div className="flex items-center gap-2">
                        <UserX className="w-5 h-5 text-red-400" />
                        <span className="text-red-400">To Deactivate</span>
                      </div>
                      <p className="text-3xl font-bold text-white mt-2">{syncPreview.users_to_deactivate}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-slate-400" />
                        <span className="text-slate-400">To Skip</span>
                      </div>
                      <p className="text-3xl font-bold text-white mt-2">{syncPreview.users_to_skip}</p>
                    </div>
                  </div>
                  
                  {syncPreview.user_preview?.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-white font-medium mb-3">User Preview</h4>
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-2">
                          {syncPreview.user_preview.map((user, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                              <div>
                                <p className="text-white font-medium">{user.name}</p>
                                <p className="text-sm text-slate-400">{user.email}</p>
                              </div>
                              <Badge className={
                                user.action === 'create' ? 'bg-emerald-900/30 text-emerald-400' :
                                user.action === 'update' ? 'bg-sky-900/30 text-sky-400' :
                                'bg-slate-800 text-slate-400'
                              }>
                                {user.action}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">Loading preview...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync History Tab */}
        <TabsContent value="history" className="mt-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-cyan-400" />
                Sync History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {syncHistory.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No sync history</p>
                  ) : (
                    syncHistory.map(job => {
                      const statusConfig = SYNC_STATUS_CONFIG[job.status] || SYNC_STATUS_CONFIG.pending;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <div key={job.id} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <StatusIcon className={`w-5 h-5 ${statusConfig.color.includes('emerald') ? 'text-emerald-400' : statusConfig.color.includes('red') ? 'text-red-400' : statusConfig.color.includes('amber') ? 'text-amber-400' : 'text-slate-400'}`} />
                              <Badge className={`${statusConfig.color} border border-current`}>
                                {statusConfig.label}
                              </Badge>
                              <Badge className="bg-slate-800 text-slate-400">
                                {job.sync_mode}
                              </Badge>
                            </div>
                            <span className="text-sm text-slate-500">
                              {new Date(job.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="grid grid-cols-4 gap-4 mt-3">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-emerald-400">{job.users_created}</p>
                              <p className="text-xs text-slate-500">Created</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-sky-400">{job.users_updated}</p>
                              <p className="text-xs text-slate-500">Updated</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-amber-400">{job.users_deactivated}</p>
                              <p className="text-xs text-slate-500">Deactivated</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-violet-400">{job.group_memberships_added}</p>
                              <p className="text-xs text-slate-500">Memberships</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Settings Dialog */}
      {editConfig && (
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-violet-400" />
                LDAP Configuration
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Server URL</Label>
                  <Input 
                    value={editConfig.server_url}
                    onChange={(e) => setEditConfig({...editConfig, server_url: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                    placeholder="ldap://ad.company.com:389"
                  />
                </div>
                <div>
                  <Label>Sync Mode</Label>
                  <Select value={editConfig.sync_mode} onValueChange={(v) => setEditConfig({...editConfig, sync_mode: v})}>
                    <SelectTrigger className="mt-1 bg-slate-950 border-slate-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {SYNC_MODES.map(mode => (
                        <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Base DN</Label>
                  <Input 
                    value={editConfig.base_dn}
                    onChange={(e) => setEditConfig({...editConfig, base_dn: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                    placeholder="DC=company,DC=com"
                  />
                </div>
                <div>
                  <Label>User Search Base</Label>
                  <Input 
                    value={editConfig.user_search_base}
                    onChange={(e) => setEditConfig({...editConfig, user_search_base: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                    placeholder="OU=Users,DC=company,DC=com"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bind DN</Label>
                  <Input 
                    value={editConfig.bind_dn}
                    onChange={(e) => setEditConfig({...editConfig, bind_dn: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                    placeholder="CN=ServiceAccount,OU=..."
                  />
                </div>
                <div>
                  <Label>Bind Password</Label>
                  <Input 
                    type="password"
                    value={editConfig.bind_password}
                    onChange={(e) => setEditConfig({...editConfig, bind_password: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                    placeholder="********"
                  />
                </div>
              </div>
              <div className="space-y-3 pt-4 border-t border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Auto-Create Users</p>
                    <p className="text-sm text-slate-500">Automatically create users found in AD</p>
                  </div>
                  <Switch 
                    checked={editConfig.auto_create_users}
                    onCheckedChange={(v) => setEditConfig({...editConfig, auto_create_users: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Auto-Deactivate Users</p>
                    <p className="text-sm text-slate-500">Deactivate users removed from AD</p>
                  </div>
                  <Switch 
                    checked={editConfig.auto_deactivate_users}
                    onCheckedChange={(v) => setEditConfig({...editConfig, auto_deactivate_users: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Auto-Sync</p>
                    <p className="text-sm text-slate-500">Automatically sync on schedule</p>
                  </div>
                  <Switch 
                    checked={editConfig.auto_sync_enabled}
                    onCheckedChange={(v) => setEditConfig({...editConfig, auto_sync_enabled: v})}
                  />
                </div>
                {editConfig.auto_sync_enabled && (
                  <div>
                    <Label>Sync Interval (hours)</Label>
                    <Input 
                      type="number"
                      value={editConfig.sync_interval_hours}
                      onChange={(e) => setEditConfig({...editConfig, sync_interval_hours: parseInt(e.target.value)})}
                      className="mt-1 bg-slate-950 border-slate-800 w-32"
                      min={1}
                      max={168}
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={handleSaveConfig} className="bg-violet-600 hover:bg-violet-700">Save Configuration</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Mapping Dialog */}
      <Dialog open={showAddMapping} onOpenChange={setShowAddMapping}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-amber-400" />
              Add Group Mapping
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>AD Group</Label>
              <Select 
                value={newMapping.ad_group_dn} 
                onValueChange={(v) => {
                  const group = adGroups.find(g => g.dn === v);
                  setNewMapping({
                    ...newMapping, 
                    ad_group_dn: v, 
                    ad_group_name: group?.name || ''
                  });
                }}
              >
                <SelectTrigger className="mt-1 bg-slate-950 border-slate-800">
                  <SelectValue placeholder="Select AD group..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {adGroups.map(group => (
                    <SelectItem key={group.dn} value={group.dn}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Security Group</Label>
              <Select 
                value={newMapping.security_group_id} 
                onValueChange={(v) => setNewMapping({...newMapping, security_group_id: v})}
              >
                <SelectTrigger className="mt-1 bg-slate-950 border-slate-800">
                  <SelectValue placeholder="Select security group..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {securityGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMapping(false)}>Cancel</Button>
            <Button onClick={handleAddGroupMapping} className="bg-amber-600 hover:bg-amber-700">Add Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
